import { type PFGrid, type Path, type Coordinates, type Location, type MapEventBlueprint, type SyncMapEventInstance, type StillMapEventInstance, type AsyncMapEventInstance, type Event, type Conditions, type StillMapEventBlueprint, type SyncMapEventBlueprint, type AsyncMapEventBlueprint} from 'src/types'

const PF = require('pathfinding')

// initialize AStar pathfinder
const finder = new PF.AStarFinder({
  allowDiagonal: false,
  dontCrossCorners: true
})

interface FindPathProps {
  startX: number
  startY: number
  endX: number
  endY: number
  grid: PFGrid
}

// findpath expects startX, startY, endX, endY, grid
const findPath = (args: FindPathProps) => {
  const { startX, startY, endX, endY, grid } = args
  const path: Path = finder.findPath(startX, startY, endX, endY, grid)
  return path
}

// Moves Events synchronously in the <Map>
// // TODO account for 'loop', 'ping-pong', 'once'
// // TODO account for obstacles blocking the path
// // TODO continue path when it's not blocked anymore
const moveEventInstances = (eventInstances: SyncMapEventInstance[] | AsyncMapEventInstance[], location: Location) => {
  const updatedInstances = eventInstances.map(instance => {
    if (instance.path.length > 0) {
      const updatedInstance = {
        ...instance,
        y: instance.path[0][1],
        x: instance.path[0][0],
        path: instance.path.slice(1)
      }
      return updatedInstance
    } else {
      const mutableGrid = createPFGrid(location.contentGrid)
      const { x, y, steps, pattern } = instance
      const checkpointIndex = steps[instance.checkpointIndex] ? instance.checkpointIndex : pattern === 'loop' ? 0 : null

      // destroy the instance to save memory ?
      if (checkpointIndex === null) {
        return instance
      }

      const endX = steps[checkpointIndex].x
      const endY = steps[checkpointIndex].y

      const newPath = findPath(
        {
          startX: x,
          startY: y,
          endX,
          endY,
          grid: mutableGrid
        })

      const updatedInstance = {
        ...instance,
        checkpointIndex: checkpointIndex + 1,
        y: newPath[1][1],
        x: newPath[1][0],
        path: newPath.slice(2)
      }
      return updatedInstance
    }
  })

  return updatedInstances
}


const checkIfWalkable = (cellContent: string) => {
  const walkableTiles = ['.']
  return walkableTiles.includes(cellContent)
}


const createPFGrid = (contentGrid: string[][]) => {
  const walkableTiles = ['.', ' ', '¦', ','] // store this somewhere

  const walkableGrid = contentGrid.map((row, y) => (
    row.map((cell, x) => {
      const ui = contentGrid[y][x]
      const isObstacle = (walkableTiles.includes(ui)) ? 0 : 1
      return isObstacle
    })
  ))

  const PFGrid: PFGrid = new PF.Grid(walkableGrid)
  return PFGrid
}

type CreateWorldProps = {
  id: string
  contentGrid: string[][]
  events: MapEventBlueprint[]
  dayLightGrid: number[][]
  nightLightGrid: number[][]
}



// initialize pathfinding grid
const createWorld = ({ id, contentGrid, events, dayLightGrid, nightLightGrid }: CreateWorldProps) => {
  const world: Location = {
    dayLightGrid,
    nightLightGrid,
    contentGrid,
    events,
    height: contentGrid.length,
    width: contentGrid[0].length,
    id
  }

  return world
}


const calculateLight = (lightCoordinates: Coordinates, cellCoordinates: Coordinates, tileLight: number, lightRadius: number) => {
  const cellLightPrevalue = (
    Math.abs(lightCoordinates.x - cellCoordinates.x) +
    Math.abs(lightCoordinates.y - cellCoordinates.y)
  ) + (4 - lightRadius)

  // min is 0 max is 5
  const cellCalculatedLight = cellLightPrevalue > 5 ? 5 : cellLightPrevalue < 0 ? 0 : cellLightPrevalue

  return (cellCalculatedLight < tileLight ? cellCalculatedLight : tileLight)
}

const literalTo2DStringArray = (stringLiteral: string) => {
  const array1D = stringLiteral.split(/\r?\n/).slice(1, -1)
  const array2D = array1D.map(line => line.split(''))
  return array2D
}

const literalTo2DNumberArray = (stringLiteral: string) => {
  const array1D = stringLiteral.split(/\r?\n/).slice(1, -1)
  const array2D: number[][] = array1D.map(line => line.split('').map(char => Number(char)))
  return array2D
}


// Movement
type Direction = 'Up' | 'Right' | 'Down' | 'Left'

const calculateNextPosition = (currentY: number, currentX: number, direction: Direction) => {
  let nextY: number = +currentY
  let nextX: number = +currentX
  
  if (direction === 'Up') {
    nextY = currentY - 1
  } else if (direction === 'Right') {
    nextX = currentX + 1
  } else if (direction === 'Down') {
    nextY = currentY + 1
  } else if (direction === 'Left') {
    nextX = currentX - 1
  }

  return {
    nextY,
    nextX
  }
}

const clampNextPosition = (nextY: number, nextX: number, grid: string[][]) => {
  const clampedY = nextY < 0 ? 0 : nextY < grid.length ? nextY : grid.length - 1
  const clampedX = nextX < 0 ? 0 : nextX < grid[0]?.length ? nextX : grid[0].length - 1
  return {
    clampedX,
    clampedY
  }
}

const findCollidingInstance = (x: number, y: number, stillMapEventInstances: StillMapEventInstance[], syncMapEventInstances: SyncMapEventInstance[], asyncMapEventInstances: AsyncMapEventInstance[]) => {
  type MapEventInstanceArray = Array<StillMapEventInstance | SyncMapEventInstance | AsyncMapEventInstance>
  const instancesToSearch: MapEventInstanceArray = [...stillMapEventInstances, ...syncMapEventInstances, ...asyncMapEventInstances]
  
  const collidingInstance = instancesToSearch.find((instance) => (instance.x === x && instance.y === y))
  return collidingInstance
}

type filterLocationEventBlueprintsProps = {
  locationEventBlueprints: MapEventBlueprint[]
  events: {[key: string]: Event}
  checkConditions: (conditions?: Conditions | undefined) => boolean
}

const filterLocationEventBlueprints = ({ locationEventBlueprints, events, checkConditions }: filterLocationEventBlueprintsProps) => {
  const filteredLocationEvents = locationEventBlueprints.filter((locationEventBlueprint) => {
    const mapEvent = events[locationEventBlueprint.id]
    if (locationEventBlueprint.type === 'event') {
      if (!mapEvent?.branches?.length) {
        return false
      }
  
      if (!mapEvent.branches.some(branch => checkConditions(branch.conditions))) {
        return false
      }
    }
    return true
  })

  return filteredLocationEvents
}

const splitLocationEventBlueprints = (locationEventBlueprints: MapEventBlueprint[]) => {
  const stillEventBlueprints: StillMapEventBlueprint[] = []
  const syncEventBlueprints: SyncMapEventBlueprint[] = []
  const asyncEventBlueprints: AsyncMapEventBlueprint[] = []

  locationEventBlueprints.forEach((content: MapEventBlueprint) => {
    if (!('steps' in content)) {
      stillEventBlueprints.push(content)
    } else if (('steps' in content && content.steps) && !('async' in content)) {
      syncEventBlueprints.push(content)
    } else if ('async' in content && content.async === true) {
      console.log(content)
      asyncEventBlueprints.push(content as AsyncMapEventBlueprint)
    } else {
      throw new Error('instance does not match any type')
    }
  })

  return {
    stillEventBlueprints,
    syncEventBlueprints,
    asyncEventBlueprints
  }
}

type instantiateLocationEventBlueprintsProps = {
  stillEventBlueprints: StillMapEventBlueprint[]
  syncEventBlueprints: SyncMapEventBlueprint[]
  asyncEventBlueprints: AsyncMapEventBlueprint[]
}

const instantiateStillMapEvents = (stillEventBlueprints: StillMapEventBlueprint[]) => stillEventBlueprints
const instantiateSyncMapEvents = (syncEventBlueprints: SyncMapEventBlueprint[]) => (
  syncEventBlueprints.map(blueprint => (
    {
      ...blueprint,
      path: [],
      checkpointIndex: 0,
      delete: false
    }
  ))
)
const instantiateAsyncMapEvents = (asyncEventBlueprints: AsyncMapEventBlueprint[]) => (
  asyncEventBlueprints.map(blueprint => (
    {
      ...blueprint,
      path: [],
      checkpointIndex: 0,
      delete: false
    }
  ))
)

const instantiateLocationEventBlueprints = ({stillEventBlueprints, syncEventBlueprints, asyncEventBlueprints}: instantiateLocationEventBlueprintsProps) => {
  const stillMapEventInstances = instantiateStillMapEvents(stillEventBlueprints)
  const syncMapEventInstances = instantiateSyncMapEvents(syncEventBlueprints)
  const asyncMapEventInstances = instantiateAsyncMapEvents(asyncEventBlueprints)

  return {
    stillMapEventInstances,
    syncMapEventInstances,
    asyncMapEventInstances
  }
}

export {
  calculateNextPosition,
  clampNextPosition,
  findCollidingInstance,
  findPath,
  createPFGrid,
  createWorld,
  calculateLight,
  literalTo2DStringArray,
  literalTo2DNumberArray,
  moveEventInstances,
  checkIfWalkable,
  filterLocationEventBlueprints,
  splitLocationEventBlueprints,
  instantiateStillMapEvents,
  instantiateSyncMapEvents,
  instantiateAsyncMapEvents,
  instantiateLocationEventBlueprints
}
